import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Sidebar from './components/Sidebar';
import Home from './pages/Home';
import LandingPage from './pages/LandingPage';
import IdeaCreate from './components/IdeaCreate';
import IdeaDashboard from './pages/IdeaDashboard';
import SharedProjectViewer from './pages/SharedProjectViewer';
import UserManagement from './components/UserManagement';
import LoginModal from './components/LoginModal';
import SignupModal from './components/SignupModal';
import ResetPasswordModal from './components/ResetPasswordModal';
import SettingsModal from './components/SettingsModal';

function ProtectedRoute({ token, children, setShowLoginModal }) {
  if (!token) {
    setShowLoginModal(true);
    return <Navigate to="/" replace />;
  }
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('access_token') || null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [refreshSidebar, setRefreshSidebar] = useState(0);
  const [inviteToken, setInviteToken] = useState('');
  const [resetToken, setResetToken] = useState('');

  const triggerSidebarRefresh = () => {
    setRefreshSidebar(prev => prev + 1);
  };

  // Component to detect signup route and handle invite tokens
  const SignupRouteHandler = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    useEffect(() => {
      const urlParams = new URLSearchParams(location.search);
      const inviteTokenFromUrl = urlParams.get('invite_token');
      
      if (inviteTokenFromUrl) {
        setInviteToken(inviteTokenFromUrl);
      }
      
      // If we're on the signup route, show the signup modal
      if (location.pathname === '/signup') {
        // If user is already logged in, we might still want to show signup for invites
        // or redirect them to home. For now, let's show signup regardless.
        setShowSignupModal(true);
        setShowLoginModal(false);
      }
    }, [location, navigate]);

    // Function to handle successful signup
    const handleSignupSuccess = () => {
      navigate('/'); // Navigate to home page
      setShowSignupModal(false);
      setTimeout(() => {
        setShowLoginModal(true);
      }, 100);
    };

    return (
      <div className="h-full overflow-y-auto">
        <Home token={token} />
      </div>
    );
  };

  // Component to handle reset password route
  const ResetPasswordRouteHandler = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    useEffect(() => {
      const urlParams = new URLSearchParams(location.search);
      const resetTokenFromUrl = urlParams.get('reset_token');
      
      if (resetTokenFromUrl) {
        setResetToken(resetTokenFromUrl);
        setShowResetPasswordModal(true);
        setShowLoginModal(false);
      }
    }, [location, navigate]);

    // Function to handle successful password reset
    const handleResetSuccess = () => {
      navigate('/'); // Navigate to home page
      setShowResetPasswordModal(false);
      setTimeout(() => {
        setShowLoginModal(true);
      }, 100);
    };

    return (
      <div className="h-full overflow-y-auto">
        <Home token={token} />
      </div>
    );
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const newToken = localStorage.getItem('access_token');
      if (newToken !== token) {
        setToken(newToken);
      }
    };

    // Check for token changes periodically (every 1 second)
    const tokenCheckInterval = setInterval(() => {
      handleStorageChange();
    }, 1000);

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(tokenCheckInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [token]);

  const openLoginModal = () => {
    setShowSignupModal(false);
    setShowResetPasswordModal(false);
    setShowLoginModal(true);
  };

  const openSignupModal = () => {
    setShowLoginModal(false);
    setShowResetPasswordModal(false);
    setShowSignupModal(true);
  };

  const openResetPasswordModal = () => {
    setShowLoginModal(false);
    setShowSignupModal(false);
    setShowResetPasswordModal(true);
  };

  return (
    <>
      <Routes>
        <Route path="/" element={
          token ? (
            <div className="flex flex-col h-screen">
              <Navbar
                token={token}
                setToken={setToken}
                openLoginModal={openLoginModal}
                openSignupModal={openSignupModal}
                openSettingsModal={() => setShowSettingsModal(true)}
              />
              <div className="flex flex-1 min-h-0">
                <Sidebar token={token} refreshKey={refreshSidebar} setToken={setToken} openSettingsModal={() => setShowSettingsModal(true)} />
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden lg:mt-4 lg:mr-4 lg:rounded-lg lg:bg-[var(--bg-primary)]">
                  <div className="h-full overflow-y-auto">
                    <Home token={token} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <LandingPage 
              openLoginModal={openLoginModal}
              openSignupModal={openSignupModal}
            />
          )
        } />
        <Route path="/ideas" element={
          <div className="flex flex-col h-screen">
            <Navbar
              token={token}
              setToken={setToken}
              openLoginModal={openLoginModal}
              openSignupModal={openSignupModal}
              openSettingsModal={() => setShowSettingsModal(true)}
            />
            <div className="flex flex-1 min-h-0">
              {token && <Sidebar token={token} refreshKey={refreshSidebar} setToken={setToken} openSettingsModal={() => setShowSettingsModal(true)} />}
              <div className="flex-1 flex flex-col min-h-0 overflow-hidden lg:mt-4 lg:mr-4 lg:rounded-lg lg:bg-[var(--bg-primary)]">
                <div className="h-full overflow-y-auto">
                  <Home token={token} />
                </div>
              </div>
            </div>
          </div>
        } />
        <Route
          path="/create-idea"
          element={
            <ProtectedRoute token={token} setShowLoginModal={setShowLoginModal}>
              <div className="flex flex-col h-screen">
                <Navbar
                  token={token}
                  setToken={setToken}
                  openLoginModal={openLoginModal}
                  openSignupModal={openSignupModal}
                  openSettingsModal={() => setShowSettingsModal(true)}
                />
                <div className="flex flex-1 min-h-0">
                  <Sidebar token={token} refreshKey={refreshSidebar} setToken={setToken} openSettingsModal={() => setShowSettingsModal(true)} />
                  <div className="flex-1 flex flex-col min-h-0 overflow-hidden lg:mt-4 lg:mr-4 lg:rounded-lg lg:bg-[var(--bg-primary)]">
                    <div className="h-full overflow-y-auto">
                      <IdeaCreate token={token} refreshSidebar={triggerSidebarRefresh} />
                    </div>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/idea/:id"
          element={
            <div className="flex flex-col h-screen">
              <Navbar
                token={token}
                setToken={setToken}
                openLoginModal={openLoginModal}
                openSignupModal={openSignupModal}
                openSettingsModal={() => setShowSettingsModal(true)}
              />
              <div className="flex flex-1 min-h-0">
                {token && <Sidebar token={token} refreshKey={refreshSidebar} setToken={setToken} openSettingsModal={() => setShowSettingsModal(true)} />}
                <div className="flex-1 flex flex-col min-h-0 overflow-hidden lg:mt-4 lg:mr-4 lg:rounded-lg lg:bg-[var(--bg-primary)]">
                  <div className="h-full overflow-y-auto">
                    <IdeaDashboard token={token} refreshSidebar={triggerSidebarRefresh} setShowLoginModal={setShowLoginModal} />
                  </div>
                </div>
              </div>
            </div>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute token={token} setShowLoginModal={setShowLoginModal}>
              <div className="flex flex-col h-screen">
                <Navbar
                  token={token}
                  setToken={setToken}
                  openLoginModal={openLoginModal}
                  openSignupModal={openSignupModal}
                  openSettingsModal={() => setShowSettingsModal(true)}
                />
                <div className="flex flex-1 min-h-0">
                  <Sidebar token={token} refreshKey={refreshSidebar} setToken={setToken} openSettingsModal={() => setShowSettingsModal(true)} />
                  <div className="flex-1 flex flex-col min-h-0 overflow-hidden lg:mt-4 lg:mr-4 lg:rounded-lg lg:bg-[var(--bg-primary)]">
                    <div className="h-full overflow-y-auto">
                      <UserManagement token={token} setShowLoginModal={setShowLoginModal} setToken={setToken} />
                    </div>
                  </div>
                </div>
              </div>
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup"
          element={<SignupRouteHandler />}
        />
        <Route
          path="/reset-password"
          element={<ResetPasswordRouteHandler />}
        />
        <Route
          path="/shared"
          element={
            <SharedProjectViewer 
              token={token}
              setShowLoginModal={setShowLoginModal}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {/* Modal components that are always available */}
      {showLoginModal && (
        <LoginModal
          setToken={setToken}
          closeModal={() => setShowLoginModal(false)}
          openSignupModal={openSignupModal}
          openResetPasswordModal={openResetPasswordModal}
        />
      )}
      {showSignupModal && (
        <SignupModal
          closeModal={() => setShowSignupModal(false)}
          openLoginModal={openLoginModal}
          initialInviteToken={inviteToken}
          currentToken={token}
          onSuccessNavigate={() => navigate('/')}
        />
      )}
      {showResetPasswordModal && (
        <ResetPasswordModal 
          closeModal={() => setShowResetPasswordModal(false)} 
          resetToken={resetToken}
          openLoginModal={openLoginModal}
          onSuccessNavigate={() => navigate('/')}
        />
      )}
      {showSettingsModal && (
        <SettingsModal
          token={token}
          closeModal={() => setShowSettingsModal(false)}
          refreshSidebar={triggerSidebarRefresh}
        />
      )}
    </>
  );
}

export default App;